import React, { useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Container,
    FormField,
    Header,
    Input,
    Link,
    Select,
    SelectProps,
    SpaceBetween,
} from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { seekingAlphaApi } from '../../client/clients';
import {
    ArticleDetailsResponse,
    SimpleArticle,
    GetArticleDetailsCredentialKeyEnum,
    GetArticleDetailsWithDirectCredentialCredentialKeyEnum,
} from '../../api';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFlashbar } from '../../context/FlashbarContext';
import { classifyError } from '../../util/errorClassifier';
import { ArticleDetailsModal } from '../alpha/ArticlePopupModal';

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

const credentialOptions: SelectProps.Option[] = [
    // F2: user-facing credential names.
    { label: 'SeekingAlpha Pro', value: GetArticleDetailsWithDirectCredentialCredentialKeyEnum.SeekingAlphaProCookie },
    { label: 'SeekingAlpha Premium', value: GetArticleDetailsWithDirectCredentialCredentialKeyEnum.SeekingAlphaPremiumCookie },
];

export const SeekingAlphaPage: React.FC = () => {
    useDocumentTitle('SeekingAlpha articles');
    const navigate = useNavigate();
    const flashbar = useFlashbar();

    const [articles, setArticles] = useState<SimpleArticle[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    const [simpleProQuantArticles, setSimpleProQuantArticles] = useState<SimpleArticle[]>([]);
    const [simpleProQuantLoading, setSimpleProQuantLoading] = useState(false);
    const [hasFetchedProQuant, setHasFetchedProQuant] = useState(false);

    const [articleDetailsLoading, setArticleDetailsLoading] = useState<string>('');
    const [articleDetailsData, setArticleDetailsData] = useState<ArticleDetailsResponse | null>(null);
    const [articleDetailsModal, setArticleDetailsModal] = useState(false);

    const [marketCapData, setMarketCapData] = useState<Record<string, number>>({});
    const [marketCapLoading, setMarketCapLoading] = useState<Record<string, boolean>>({});
    const [marketCapError, setMarketCapError] = useState<Record<string, string>>({});

    const [directArticleId, setDirectArticleId] = useState('');
    const [directCredential, setDirectCredential] = useState<SelectProps.Option>(credentialOptions[1]);
    const [directArticleLoading, setDirectArticleLoading] = useState(false);
    const [directArticleIdError, setDirectArticleIdError] = useState<string | undefined>(undefined);

    const [forceAiVerification, setForceAiVerification] = useState(true);
    const [directForceAiVerification, setDirectForceAiVerification] = useState(true);

    const handleFetchArticles = async () => {
        setLoading(true);
        try {
            const response = await seekingAlphaApi.getLatestPublishedStock();
            setArticles(response.data);
            setHasFetched(true);
            flashbar.push({
                type: 'success',
                header: 'Articles loaded',
                content: `Fetched ${response.data.length} article${response.data.length === 1 ? '' : 's'}.`,
            });
            if (response.data && response.data.length > 0) {
                loadMarketCapsForFirstArticle(response.data);
            }
        } catch (err: unknown) {
            console.error('Error fetching articles:', err);
            const classified = classifyError(err, { onRetry: handleFetchArticles });
            flashbar.push({
                type: 'error',
                header: 'Failed to fetch articles',
                content: classified.userMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFetchSimpleProQuantPortfolio = async () => {
        setSimpleProQuantLoading(true);
        try {
            const response = await seekingAlphaApi.getSimpleProQuantPortfolioArticles();
            setSimpleProQuantArticles(response.data);
            setHasFetchedProQuant(true);
            flashbar.push({
                type: 'success',
                header: 'Pro Quant Portfolio loaded',
                content: `Fetched ${response.data.length} article${response.data.length === 1 ? '' : 's'}.`,
            });
            if (response.data && response.data.length > 0) {
                loadMarketCapsForFirstArticle(response.data);
            }
        } catch (err: unknown) {
            console.error('Error fetching Simple Pro Quant Portfolio:', err);
            const classified = classifyError(err, { onRetry: handleFetchSimpleProQuantPortfolio });
            flashbar.push({
                type: 'error',
                header: 'Failed to fetch Pro Quant Portfolio',
                content: classified.userMessage,
            });
        } finally {
            setSimpleProQuantLoading(false);
        }
    };

    const handleGetMarketCap = async (ticker: string) => {
        if (marketCapLoading[ticker] || marketCapData[ticker]) return;
        setMarketCapLoading((prev) => ({ ...prev, [ticker]: true }));
        setMarketCapError((prev) => ({ ...prev, [ticker]: '' }));
        try {
            const response = await seekingAlphaApi.getMarketCap(ticker);
            setMarketCapData((prev) => ({ ...prev, [ticker]: response.data }));
        } catch (err: any) {
            console.error(`Error fetching market cap for ${ticker}:`, err);
            const errorMessage = err.response?.status === 404 ? 'Not available' : 'Error';
            setMarketCapError((prev) => ({ ...prev, [ticker]: errorMessage }));
        } finally {
            setMarketCapLoading((prev) => ({ ...prev, [ticker]: false }));
        }
    };

    const loadMarketCapsForFirstArticle = async (list: SimpleArticle[]) => {
        const first = list.find((a) => a.tickers && a.tickers.length > 0);
        if (!first) return;
        const tickers = first.tickers || [];
        for (let i = 0; i < tickers.length; i++) {
            if (i > 0) {
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
            handleGetMarketCap(tickers[i]);
        }
    };

    const formatMarketCap = (marketCap: number): string => {
        if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(1)}T`;
        if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(1)}B`;
        if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(1)}M`;
        if (marketCap >= 1e3) return `$${(marketCap / 1e3).toFixed(1)}K`;
        return `$${marketCap.toFixed(0)}`;
    };

    const handleLoadArticleDetails = async (articleId: string, isFromProQuantPortfolio: boolean) => {
        setArticleDetailsLoading(articleId);
        try {
            const credentialKey = isFromProQuantPortfolio
                ? GetArticleDetailsCredentialKeyEnum.SeekingAlphaProCookie
                : GetArticleDetailsCredentialKeyEnum.SeekingAlphaPremiumCookie;
            const response = await seekingAlphaApi.getArticleDetails(credentialKey, articleId, forceAiVerification);
            setArticleDetailsData(response.data);
            setArticleDetailsModal(true);
        } catch (err: unknown) {
            console.error('Error fetching article details:', err);
            const classified = classifyError(err);
            flashbar.push({
                type: 'error',
                header: 'Failed to fetch article details',
                content: classified.userMessage,
            });
        } finally {
            setArticleDetailsLoading('');
        }
    };

    const handleDirectArticleLookup = async () => {
        if (!directArticleId.trim()) {
            // F7: surface the missing-input error in the FormField, not as a toast.
            setDirectArticleIdError('Article ID is required.');
            return;
        }
        setDirectArticleIdError(undefined);
        setDirectArticleLoading(true);
        try {
            const response = await seekingAlphaApi.getArticleDetailsWithDirectCredential(
                directCredential.value as GetArticleDetailsWithDirectCredentialCredentialKeyEnum,
                directArticleId.trim(),
                directForceAiVerification
            );
            setArticleDetailsData(response.data);
            setArticleDetailsModal(true);
        } catch (err: unknown) {
            console.error('Error fetching direct article details:', err);
            const classified = classifyError(err, { onRetry: handleDirectArticleLookup });
            flashbar.push({
                type: 'error',
                header: 'Failed to fetch article details',
                content: classified.userMessage,
            });
        } finally {
            setDirectArticleLoading(false);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Unknown date';
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    return (
        <Container>
            <SpaceBetween size="l">
                <Header
                    variant="h1"
                    description="Fetch the latest published stock articles from SeekingAlpha."
                    actions={
                        <Button
                            iconName="refresh"
                            variant="icon"
                            ariaLabel="Refresh"
                            loading={loading}
                            onClick={handleFetchArticles}
                        />
                    }
                >
                    SeekingAlpha articles
                </Header>

                {/* Fetch controls */}
                <SpaceBetween size="m" direction="horizontal">
                    <Button
                        variant="primary"
                        loading={loading}
                        onClick={handleFetchArticles}
                    >
                        {loading ? 'Fetching…' : 'Fetch Articles'}
                    </Button>
                    <Button
                        loading={simpleProQuantLoading}
                        onClick={handleFetchSimpleProQuantPortfolio}
                    >
                        {simpleProQuantLoading ? 'Fetching…' : 'Fetch Simple Pro Quant Portfolio'}
                    </Button>
                </SpaceBetween>

                {/* F1: rename "Force Bedrock AI Verification" → "Re-run AI verification". */}
                <Checkbox
                    checked={forceAiVerification}
                    onChange={({ detail }) => setForceAiVerification(detail.checked)}
                >
                    Re-run AI verification
                </Checkbox>

                {/* Direct article lookup */}
                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Look up a specific article by ID, bypassing the cache."
                        >
                            Direct article lookup
                        </Header>
                    }
                >
                    <SpaceBetween size="m">
                        <FormField
                            label="Article ID"
                            constraintText="Article ID is required"
                            errorText={directArticleIdError}
                        >
                            <Input
                                value={directArticleId}
                                onChange={({ detail }) => {
                                    setDirectArticleId(detail.value);
                                    if (detail.value.trim()) setDirectArticleIdError(undefined);
                                }}
                                onKeyDown={({ detail }) => {
                                    if (detail.key === 'Enter') handleDirectArticleLookup();
                                }}
                                placeholder="e.g. 4737412"
                            />
                        </FormField>
                        <FormField label="Credential">
                            <Select
                                selectedOption={directCredential}
                                onChange={({ detail }) =>
                                    setDirectCredential(detail.selectedOption as SelectProps.Option)
                                }
                                options={credentialOptions}
                            />
                        </FormField>
                        <Checkbox
                            checked={directForceAiVerification}
                            onChange={({ detail }) => setDirectForceAiVerification(detail.checked)}
                        >
                            Re-run AI verification
                        </Checkbox>
                        <Button
                            variant="primary"
                            loading={directArticleLoading}
                            onClick={handleDirectArticleLookup}
                            disabled={directArticleLoading}
                        >
                            {directArticleLoading ? 'Loading…' : 'Fetch Article'}
                        </Button>
                    </SpaceBetween>
                </Container>

                {/* Article columns */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    {/* Regular articles column */}
                    <SpaceBetween size="m">
                        <Header variant="h2" counter={`(${articles.length})`}>
                            Articles
                        </Header>
                        {articles.length === 0 ? (
                            // F8: empty-state copy with link to credentials.
                            <Box textAlign="center" color="text-status-inactive" padding="l">
                                <SpaceBetween size="xs">
                                    <Box variant="strong">No articles loaded yet.</Box>
                                    <Box>
                                        Click <strong>Fetch Articles</strong> to pull the latest. If fetches keep
                                        coming back empty, check your{' '}
                                        <Link
                                            href="/credentials"
                                            onFollow={(event) => {
                                                event.preventDefault();
                                                navigate('/credentials');
                                            }}
                                        >
                                            SeekingAlpha credentials
                                        </Link>
                                        .
                                    </Box>
                                    {hasFetched && (
                                        <Box variant="small" color="text-body-secondary">
                                            Last fetch returned no results.
                                        </Box>
                                    )}
                                </SpaceBetween>
                            </Box>
                        ) : (
                            <SpaceBetween size="s">
                                {articles.map((article, index) => (
                                    <ArticleCard
                                        key={article.id || `article-${index}`}
                                        article={article}
                                        marketCapData={marketCapData}
                                        marketCapLoading={marketCapLoading}
                                        marketCapError={marketCapError}
                                        formatMarketCap={formatMarketCap}
                                        formatDate={formatDate}
                                        onTickerClick={handleGetMarketCap}
                                        onLoadDetails={(id) => handleLoadArticleDetails(id, false)}
                                        loadingId={articleDetailsLoading}
                                    />
                                ))}
                            </SpaceBetween>
                        )}
                    </SpaceBetween>

                    {/* Pro Quant column */}
                    <SpaceBetween size="m">
                        <Header variant="h2" counter={`(${simpleProQuantArticles.length})`}>
                            Simple Pro Quant Portfolio
                        </Header>
                        {simpleProQuantArticles.length === 0 ? (
                            // F5: explain why this list is sparse.
                            <Box textAlign="center" color="text-status-inactive" padding="l">
                                <SpaceBetween size="xs">
                                    <Box variant="strong">
                                        Click "Fetch Simple Pro Quant Portfolio" to load.
                                    </Box>
                                    <Box>This list updates infrequently — once per quarter.</Box>
                                    {hasFetchedProQuant && (
                                        <Box variant="small" color="text-body-secondary">
                                            Last fetch returned no results.
                                        </Box>
                                    )}
                                </SpaceBetween>
                            </Box>
                        ) : (
                            <SpaceBetween size="s">
                                {simpleProQuantArticles.map((article, index) => (
                                    <ArticleCard
                                        key={article.id || `pq-${index}`}
                                        article={article}
                                        accent="#6f42c1"
                                        marketCapData={marketCapData}
                                        marketCapLoading={marketCapLoading}
                                        marketCapError={marketCapError}
                                        formatMarketCap={formatMarketCap}
                                        formatDate={formatDate}
                                        onTickerClick={handleGetMarketCap}
                                        onLoadDetails={(id) => handleLoadArticleDetails(id, true)}
                                        loadingId={articleDetailsLoading}
                                    />
                                ))}
                            </SpaceBetween>
                        )}
                    </SpaceBetween>
                </div>

                {articleDetailsModal && articleDetailsData && (
                    <ArticleDetailsModal
                        data={articleDetailsData}
                        onClose={() => {
                            setArticleDetailsModal(false);
                            setArticleDetailsData(null);
                        }}
                    />
                )}
            </SpaceBetween>
        </Container>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Article card — F6 hides the article ID; the title is the click target.
// ─────────────────────────────────────────────────────────────────────────────

interface ArticleCardProps {
    article: SimpleArticle;
    accent?: string;
    marketCapData: Record<string, number>;
    marketCapLoading: Record<string, boolean>;
    marketCapError: Record<string, string>;
    formatMarketCap: (n: number) => string;
    formatDate: (s?: string) => string;
    onTickerClick: (ticker: string) => void;
    onLoadDetails: (id: string) => void;
    loadingId: string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
    article,
    accent = '#0972d3',
    marketCapData,
    marketCapLoading,
    marketCapError,
    formatMarketCap,
    formatDate,
    onTickerClick,
    onLoadDetails,
    loadingId,
}) => {
    const isLoading = !!article.id && loadingId === article.id;
    return (
        <div
            style={{
                padding: 16,
                border: `1px solid ${accent}33`,
                borderLeft: `4px solid ${accent}`,
                borderRadius: 8,
                backgroundColor: '#ffffff',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <button
                    type="button"
                    onClick={() => article.id && onLoadDetails(article.id)}
                    disabled={!article.id || isLoading}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        textAlign: 'left',
                        font: 'inherit',
                        color: accent,
                        cursor: article.id ? 'pointer' : 'default',
                        fontSize: 16,
                        fontWeight: 600,
                        flex: 1,
                    }}
                >
                    {article.title || 'Untitled Article'}
                </button>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {article.tickers && article.tickers.length > 0 ? (
                        article.tickers.map((ticker, tickerIndex) => (
                            <div
                                key={`${ticker}-${tickerIndex}`}
                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                            >
                                <button
                                    type="button"
                                    onClick={() => onTickerClick(ticker)}
                                    aria-label={`Load market cap for ${ticker}`}
                                    style={{
                                        backgroundColor: accent,
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: 4,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        border: 'none',
                                    }}
                                >
                                    {ticker}
                                </button>
                                <div style={{ fontSize: 10, minHeight: 12 }}>
                                    {marketCapLoading[ticker] ? (
                                        <span style={{ color: '#5f6b7a' }}>Loading…</span>
                                    ) : marketCapError[ticker] ? (
                                        <span style={{ color: '#b30000' }}>{marketCapError[ticker]}</span>
                                    ) : marketCapData[ticker] ? (
                                        <span style={{ color: '#1d8102', fontWeight: 700 }}>
                                            {formatMarketCap(marketCapData[ticker])}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#5f6b7a' }}>Click for cap</span>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <span style={{ color: '#5f6b7a', fontSize: 12 }}>No tickers</span>
                    )}
                </div>
            </div>

            {/* Optional summary */}
            {(article as any).summary && Array.isArray((article as any).summary) && (article as any).summary.length > 0 && (
                <ul style={{ marginTop: 8, paddingLeft: 20, fontSize: 13, color: '#414d5c' }}>
                    {(article as any).summary.map((point: string, idx: number) => (
                        <li key={idx} style={{ marginBottom: 2 }}>{point}</li>
                    ))}
                </ul>
            )}

            <div style={{ marginTop: 8, fontSize: 12, color: '#5f6b7a', display: 'flex', gap: 16 }}>
                <span>Published: {formatDate(article.publishDate)}</span>
                {article.id && <span>ID: <code style={{ fontSize: 11 }}>{article.id}</code></span>}
            </div>

            <div style={{ marginTop: 10 }}>
                <Button
                    variant="link"
                    onClick={() => article.id && onLoadDetails(article.id)}
                    loading={isLoading}
                    disabled={!article.id || isLoading}
                >
                    {!article.id ? 'No ID' : isLoading ? 'Loading…' : 'View details'}
                </Button>
            </div>
        </div>
    );
};
